import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  map,
  distinctUntilChanged,
  Subject,
  takeUntil,
  Subscription,
  merge,
  startWith,
  subscribeOn,
  asapScheduler,
} from 'rxjs';
import { ValueItem } from 'app/shared/interfaces/value-item.interface';
import {
  MapFilterItem,
  MapFilterType,
} from 'app/shared/components/main-map/components/search-results/map-filter-state.model';
import { MapFilterManager } from '../../map-filter.manager';

interface ObjectIdForm {
  idobjeto: FormControl<string>;
}

@Component({
  selector: 'app-object-id-filter-form',
  templateUrl: 'object-id-filter-form.component.html',
  styleUrls: ['object-id-filter-form.component.scss'],
})
export class ObjectIdFilterFormComponent implements OnInit, OnDestroy {
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  formGroup: FormGroup<ObjectIdForm>;

  districtList: ValueItem<string>[];
  countyList: ValueItem<string>[];
  parishList: ValueItem<string>[];

  private filterList: MapFilterItem[] = [];
  private newFiltersMap = new Map<string, any>();
  private filterKeySetRemoved = new Set<string>();

  private filtersSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mapFilterManager: MapFilterManager
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      idobjeto: this.formBuilder.control<string>(null),
    });

    this.observeFormValueRangeChanges(['idobjeto'])
      .pipe(takeUntil(this.lifecycle))
      .subscribe(({ fieldName, value }) => {
        if (value === null || value === undefined || value === '') {
          this.newFiltersMap.delete(fieldName);
          this.filterKeySetRemoved.add(fieldName);
        } else {
          this.newFiltersMap.set(fieldName, value);
          this.filterKeySetRemoved.delete(fieldName);
        }
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onShowingInternal() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.mapFilterManager.onFiltersChanged
      .pipe(subscribeOn(asapScheduler), takeUntil(this.lifecycle))
      .subscribe((list) => {
        this.filterList = list.filter((x) => x.type === MapFilterType.ObjectId);

        this.handleValues();
      });
  }

  onHiddenInternal() {
    this.filtersSubscription?.unsubscribe();
    this.handleValues();
  }

  onSubmit(e) {
    e?.preventDefault();

    const type = MapFilterType.ObjectId;
    const addsOrUpdates = Array.from(this.newFiltersMap, ([field, value]) => {
      let item = this.mapFilterManager.getFilterByTypeAndField(type, field);
      if (!item) {
        item = new MapFilterItem({ type, field });
      }

      const valueStr = MapFilterItem.stringfyValue(value);
      item.text = valueStr;
      item.valueAsStr = valueStr;
      item.valueType = MapFilterItem.getValueType(value);

      return item;
    });
    const deletes = Array.from(this.filterKeySetRemoved.values(), (field) => {
      return this.mapFilterManager.getFilterKeyBy({ type, field });
    }).filter((x) => !!x);

    this.mapFilterManager.applyEditsFilters({
      adds: addsOrUpdates,
      updates: addsOrUpdates,
      deletes,
    });

    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnClearClick() {
    this.resetValues();
  }

  private handleValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();

    const values = this.filterList.reduce((acc, x) => {
      acc[x.field] = x.value;
      return acc;
    }, {});

    this.formGroup.reset(values);
  }

  private resetValues() {
    this.filterKeySetRemoved.clear();
    this.newFiltersMap.clear();
    this.formGroup.reset();
  }

  private observeFormValueRangeChanges(fieldNames: Array<keyof ObjectIdForm>) {
    return merge(
      ...fieldNames.map((fieldName) => {
        return this.observeFormValueChanges(fieldName).pipe(
          map((value) => {
            return { fieldName, value };
          })
        );
      })
    );
  }

  private observeFormValueChanges(fieldName: keyof ObjectIdForm) {
    const control = this.formGroup.get(fieldName);
    const initialValue = control.value;
    return control.valueChanges.pipe(
      startWith(initialValue),
      distinctUntilChanged()
    );
  }
}
